/**
 * CxL3 — MV switchboards.
 *
 * **Taken from the CxA's issued script**, `MY02_ELEC_CxL3_SUT_MVSB_R02_CxA`,
 * revision 02, 23/09/2026 — not from a draft. It replaces the speculative MVSB
 * catalogue sketched in `equipment-silo.md`, which was considerably wider than
 * what the CxA actually asks for.
 *
 * What the issued script contains: three checklists, a voltage test, the 29-channel
 * meter verification, and a very large EPMS/BMS point verification — 123 points
 * across four devices. What it does **not** contain, and what the draft wrongly
 * assumed: circuit-breaker operation, earth-switch operation, CB–ES interlocking,
 * SF6 monitoring, tripping DC supply, relay functional trips, thermography.
 *
 * That absence is recorded rather than filled in. Adding tests the CxA did not ask
 * for turns a passing board into an argument, and the CB–ES interlock in
 * particular is a safety question that belongs to them, not to us. It is on the
 * list to raise, in `mvsb-script.md`.
 *
 * Three defects in the issued script are handled here and flagged in that doc:
 *
 *  1. `Voltage (1-2)` appears **twice** in the meter table and `Voltage (3-1)` is
 *     missing. Reproducing that verbatim means 3-1 is never measured, so the
 *     catalogue measures 3-1 and the report prints the CxA's label.
 *  2. `Power Factor (total)` carries a **0.0%** passing criterion — the two meters
 *     must agree exactly, so every board fails that line.
 *  3. The voltage test asks for phase-to-neutral and phase-to-earth **in volts** on
 *     an MV board, while the startup checklist records kV. Left as the CxA wrote
 *     it, marked N/A-able, pending their answer.
 */

import type { CatalogueCheck, CatalogueTest } from '../catalogue.js';

/** The CxA's passing percentages, channel by channel. */
const METER_CHANNELS: readonly (readonly [string, string, number])[] = [
  ['Voltage (1-2)', 'V', 0.5],
  ['Voltage (2-3)', 'V', 0.5],
  // The script repeats "Voltage (1-2)" here. Measured as 3-1; printed as they label it.
  ['Voltage (3-1)', 'V', 0.5],
  ['V(1-N)', 'V', 0.5],
  ['V(2-N)', 'V', 0.5],
  ['V(3-N)', 'V', 0.5],
  ['Current (L1)', 'A', 1.5],
  ['Current (L2)', 'A', 1.5],
  ['Current (L3)', 'A', 1.5],
  ['Active power kW (L1)', 'kW', 2.0],
  ['Active power kW (L2)', 'kW', 2.0],
  ['Active power kW (L3)', 'kW', 2.0],
  ['Active power kW (Total)', 'kW', 2.0],
  ['Apparent power kVA (L1)', 'kVA', 2.0],
  ['Apparent power kVA (L2)', 'kVA', 2.0],
  ['Apparent power kVA (L3)', 'kVA', 2.0],
  ['Apparent power kVA (Total)', 'kVA', 2.0],
  ['Reactive power kVAR (L1)', 'kVAR', 3.0],
  ['Reactive power kVAR (L2)', 'kVAR', 3.0],
  ['Reactive power kVAR (L3)', 'kVAR', 3.0],
  ['Frequency', 'Hz', 0.1],
  ['Power Factor (total)', '', 0.0],
  ['THDv (L1)', '%', 5.0],
  ['THDv (L2)', '%', 5.0],
  ['THDv (L3)', '%', 5.0],
  ['THDi (L1)', '%', 5.0],
  ['THDi (L2)', '%', 5.0],
  ['THDi (L3)', '%', 5.0],
];

const yesNo = (label: string, requiresPhoto = false): CatalogueCheck => ({
  label,
  responseType: 'yes_no_applicable',
  toleranceMode: 'none',
  ...(requiresPhoto ? { requiresPhoto: true } : {}),
});

/** A BMS point: the value on the screen against the value at the device. */
const point = (label: string, remark?: string): CatalogueCheck => ({
  label,
  responseType: 'point_verification',
  toleranceMode: 'none',
  ...(remark ? { acceptanceCriteria: remark } : {}),
});

const STATE = '0=No; 1=Yes';
const ALARM = '0=Normal; 1=Alarm';
const COMMS = '0=Normal; 1=Comloss';

const bmsTest = (
  code: string,
  device: string,
  points: readonly CatalogueCheck[],
  alarms: readonly CatalogueCheck[],
): CatalogueTest => ({
  code,
  name: `EPMS/BMS point verification — ${device}`,
  cxLevel: 'CxL3',
  equipmentTypes: ['MV Switchboard'],
  scopes: ['board'],
  defaultSelected: true,
  section: 'Functional Check with EPMS/BMS',
  note: `${points.length} points and ${alarms.length} alarms, each read on screen and at the device.`,
  checks: [...points, ...alarms],
  shotCodes: [`MVSB-BMS-${code.split('-').pop()}`],
});

export const CXL3_MV_SWITCHBOARD: readonly CatalogueTest[] = [
  {
    code: 'MVSB-PRE',
    name: 'Pre-startup checklist',
    cxLevel: 'CxL3',
    equipmentTypes: ['MV Switchboard'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Pre-Startup Checklist',
    checks: [
      yesNo('L2 installation check completed and documentation uploaded'),
      yesNo('Cabinet body undamaged; levelness and perpendicularity meet requirements; doors close tightly; internal components firmly installed', true),
      yesNo('Input and output cables correct type and specification; connections secure; busbar joints tight and unoxidised; terminal wiring correct and marked', true),
      yesNo('Equipment well earthed and earthing resistance meets the relevant standard', true),
      yesNo('Circuit breakers, contactors and relays undamaged, complete, and rated as designed'),
      yesNo('Exterior clean and free of trash, metal shavings or stripped insulation'),
      yesNo('Energised and started per approved MOP, site electrical safety programme and manufacturer procedure'),
    ],
    shotCodes: ['MVSB-PRE-CABINET', 'MVSB-PRE-BUSBAR', 'MVSB-PRE-EARTH'],
  },
  {
    code: 'MVSB-START',
    name: 'Startup checklist',
    cxLevel: 'CxL3',
    equipmentTypes: ['MV Switchboard'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Startup Checklist',
    note: 'Both utility sources, each with its own position-signal readings. The script records these in kV.',
    checks: [
      yesNo('Phase sequences consistent from A/B mains and busbar voltage difference within the allowable range'),
      yesNo('Source A: phase sequence and power confirmed and measured', true),
      yesNo('Source A: switchgear position signal normal with parameters displayed'),
      { label: 'Source A — Voltage L1-L2', responseType: 'measured_pass_fail', unit: 'kV', toleranceMode: 'none' },
      { label: 'Source A — Voltage L1-L3', responseType: 'measured_pass_fail', unit: 'kV', toleranceMode: 'none' },
      { label: 'Source A — Voltage L2-L3', responseType: 'measured_pass_fail', unit: 'kV', toleranceMode: 'none' },
      { label: 'Source A — Current L1', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none' },
      { label: 'Source A — Current L2', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none' },
      { label: 'Source A — Current L3', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none' },
      { label: 'Source A — Frequency', responseType: 'measured_pass_fail', unit: 'Hz', nominal: 50, tolerance: 1, toleranceMode: 'percent' },
      yesNo('Source B: phase sequence and power confirmed and measured', true),
      yesNo('Source B: switchgear position signal normal with parameters displayed'),
      { label: 'Source B — Voltage L1-L2', responseType: 'measured_pass_fail', unit: 'kV', toleranceMode: 'none' },
      { label: 'Source B — Voltage L1-L3', responseType: 'measured_pass_fail', unit: 'kV', toleranceMode: 'none' },
      { label: 'Source B — Voltage L2-L3', responseType: 'measured_pass_fail', unit: 'kV', toleranceMode: 'none' },
      { label: 'Source B — Current L1', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none' },
      { label: 'Source B — Current L2', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none' },
      { label: 'Source B — Current L3', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none' },
      { label: 'Source B — Frequency', responseType: 'measured_pass_fail', unit: 'Hz', nominal: 50, tolerance: 1, toleranceMode: 'percent' },
    ],
    shotCodes: ['MVSB-START-PR-A', 'MVSB-START-PR-B', 'MVSB-START-DISPLAY'],
  },
  {
    code: 'MVSB-PREFUNC',
    name: 'Pre-function test checklist',
    cxLevel: 'CxL3',
    equipmentTypes: ['MV Switchboard'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Pre-Function Test Checklist',
    checks: [
      yesNo('Communication between the local multifunctional meter and EPMS is normal'),
      yesNo('Alarm point displays on both the local control panel and the EBMS (if applicable)'),
      yesNo('All circuit breakers turned on and off and their status confirmed on the EPMS', true),
      yesNo('All protection functions approved by the design have been activated'),
      yesNo('Equipment supplier inspected and verified the setting values of all circuit breakers', true),
      yesNo('Protection relay setting and verification test has been completed'),
    ],
    shotCodes: ['MVSB-PF-EPMS', 'MVSB-PF-SETTINGS'],
  },
  {
    code: 'MVSB-VT',
    name: 'Voltage test',
    cxLevel: 'CxL3',
    equipmentTypes: ['MV Switchboard'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Voltage Test',
    note: 'The issued script asks for these in volts, including phase-to-neutral and phase-to-earth. Most MV boards are three-wire with no distributed neutral — query with the CxA and mark N/A where it does not exist.',
    checks: [
      { label: 'L1-L2', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L2-L3', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L3-L1', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L1-N', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L2-N', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L3-N', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L1-E', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L2-E', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
      { label: 'L3-E', responseType: 'measured_pass_fail', unit: 'V', toleranceMode: 'none' },
    ],
    shotCodes: ['MVSB-VT-METER'],
  },
  {
    code: 'MVSB-MV',
    name: 'Power meter reading test',
    cxLevel: 'CxL3',
    equipmentTypes: ['MV Switchboard'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Power Meter Reading Test',
    note: 'One table per panel, as the script sets it out. Power Factor (total) carries a 0.0% band in the issued revision and will always fail — raised with the CxA.',
    checks: METER_CHANNELS.map(([label, unit, tol]) => ({
      label,
      responseType: 'meter_verification' as const,
      ...(unit ? { unit } : {}),
      tolerance: tol,
      toleranceMode: 'percent' as const,
      ...(tol === 0 ? { acceptanceCriteria: '0.0% in the issued script — flagged, awaiting a workable band' } : {}),
    })),
    shotCodes: ['MVSB-MV-DPM', 'MVSB-MV-PQA', 'MVSB-MV-BMS'],
  },
  bmsTest(
    'MVSB-BMS-ISO',
    'HV Isolation',
    [
      point('Phase Voltage Ua'), point('Phase Voltage Ub'), point('Phase Voltage Uc'),
      point('Phase Current Ia'), point('Phase Current Ib'), point('Phase Current Ic'),
      point('Frequency f'), point('Active Power P'), point('Power Factor Pf'),
      point('Line Voltage Uab'), point('Line Voltage Ubc'), point('Line Voltage Uca'),
    ],
    [point('Switchgear Connected', STATE), point('Device Communication Status', COMMS)],
  ),
  bmsTest(
    'MVSB-BMS-INC',
    'HV Incoming',
    [
      point('Phase Voltage Ua (kV)'), point('Phase Voltage Ub (kV)'), point('Phase Voltage Uc (kV)'),
      point('Line Voltage Uab (kV)'), point('Line Voltage Ubc (kV)'), point('Line Voltage Uca (kV)'),
      point('Phase Current Ia (A)'), point('Phase Current Ib (A)'), point('Phase Current Ic (A)'),
      point('Frequency f (Hz)'),
      point('Total Active Power Psum (kW)'), point('Active Power Pa (kW)'), point('Active Power Pb (kW)'), point('Active Power Pc (kW)'),
      point('Total Reactive Power Qsum (kvar)'), point('Reactive Power Qa (kvar)'), point('Reactive Power Qb (kvar)'), point('Reactive Power Qc (kvar)'),
      point('Total Apparent Power Ssum (kVA)'), point('Apparent Power Sa (kVA)'), point('Apparent Power Sb (kVA)'), point('Apparent Power Sc (kVA)'),
      point('Power Factor Pf'),
      point('Active Energy Delivered (kWh)'), point('Active Energy Received (kWh)'),
      point('Reactive Energy Delivered (kvarh)'), point('Reactive Energy Received (kvarh)'),
      point('Zero-sequence current In (A)'),
      point('Power Factor Phase A'), point('Power Factor Phase B'), point('Power Factor Phase C'),
      point('Pa Real-time demand (kW)'), point('Pb Real-time demand (kW)'), point('Pc Real-time demand (kW)'), point('P Total Real-time demand (kW)'),
      point('Qa Real-time demand (kvar)'), point('Qb Real-time demand (kvar)'), point('Qc Real-time demand (kvar)'), point('Q Total Real-time demand (kvar)'),
      point('Sa Real-time demand (kVA)'), point('Sb Real-time demand (kVA)'), point('Sc Real-time demand (kVA)'), point('S Total Real-time demand (kVA)'),
      point('P Total maximum demand this month (kW)'), point('Q Total maximum demand this month (kvar)'), point('S Total maximum demand this month (kVA)'),
      point('P Total maximum demand last month (kW)'), point('Q Total maximum demand last month (kvar)'), point('S Total maximum demand last month (kVA)'),
      point('Load Rate (%)'),
    ],
    [
      point('Switchgear Open', STATE), point('Switchgear Close', STATE), point('Switchgear Connected', STATE),
      point('Remote/Local switch State', STATE), point('Switchgear spring discharged', STATE),
      point('Stage 1 Over Current Protection', ALARM), point('Stage 2 Over Current Protection', ALARM),
      point('Stage 1 Zero-Sequence Current Protection', ALARM),
      point('Device Communication Status', COMMS),
    ],
  ),
  bmsTest(
    'MVSB-BMS-PT',
    'HV PT',
    [
      point('Phase Voltage Ua (V)'), point('Phase Voltage Ub (V)'), point('Phase Voltage Uc (V)'),
      point('Line Voltage Uab (V)'), point('Line Voltage Ubc (V)'), point('Line Voltage Uca (V)'),
      point('Frequency f (Hz)'),
    ],
    // The script writes this one's convention the other way round to every other
    // comms point in the document. Reproduced as issued, and queried.
    [point('Device Communication Status', '0=Comloss; 1=Normal — inverted against every other comms point in the script')],
  ),
  bmsTest(
    'MVSB-BMS-OUT',
    'HV Output',
    [
      point('Phase Voltage Ua (kV)'), point('Phase Voltage Ub (kV)'), point('Phase Voltage Uc (kV)'),
      point('Line Voltage Uab (kV)'), point('Line Voltage Ubc (kV)'), point('Line Voltage Uca (kV)'),
      point('Phase Current Ia (A)'), point('Phase Current Ib (A)'), point('Phase Current Ic (A)'),
      point('Frequency f (Hz)'),
      point('Total Active Power Psum (kW)'), point('Active Power Pa (kW)'), point('Active Power Pb (kW)'), point('Active Power Pc (kW)'),
      point('Total Reactive Power Qsum (kVar)'), point('Reactive Power Qa (kVar)'), point('Reactive Power Qb (kVar)'), point('Reactive Power Qc (kVar)'),
      point('Total Apparent Power Ssum (kVA)'), point('Apparent Power Sa (kVA)'), point('Apparent Power Sb (kVA)'), point('Apparent Power Sc (kVA)'),
      point('Power Factor Pf'),
      point('Active Energy Delivered (kWh)'), point('Active Energy Received (kWh)'),
      point('Reactive Energy Delivered (kVarh)'), point('Reactive Energy Received (kVarh)'),
      point('Power Factor Phase A'), point('Power Factor Phase B'), point('Power Factor Phase C'),
      point('Zero-sequence current In'), point('Zero-sequence voltage Vn'),
    ],
    [
      point('Switchgear Open', STATE), point('Switchgear Close', STATE), point('Earth Switch Status', STATE),
      point('Switchgear Connected', STATE), point('Remote/Local switch State', STATE),
      point('Switchgear spring discharged', STATE),
      point('Stage 1 Over Current Protection', ALARM), point('Stage 2 Over Current Protection', ALARM),
      point('Stage 1 Zero-Sequence Current Protection', ALARM),
      point('Device Communication Status', COMMS),
    ],
  ),
];

/** What the issued script asks for, as a number, so the doc and the code agree. */
export const MVSB_SCRIPT = {
  reference: 'MY02_ELEC_CxL3_SUT_MVSB_R02_CxA',
  revision: '02',
  issued: '2026-09-23',
  meterChannels: METER_CHANNELS.length,
} as const;
